import { Component, inject, signal, OnInit , ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '@core/services/auth.service';
import { ResponsiveLayoutService } from '@core/services/responsive-layout.service';
import { User } from '@core/models/user.model';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';

/**
 * Application header with navigation and user menu
 * Includes responsive hamburger menu for mobile/tablet views
 */
@Component({
  selector: 'app-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, RouterLinkActive, MatTooltipModule, ClickOutsideDirective],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  public responsiveService = inject(ResponsiveLayoutService);

  user = signal<User | null>(null);
  showUserMenu = signal(false);
  showMobileMenu = signal(false);

  ngOnInit(): void {
    this.authService.currentUser$.subscribe((user: any) => {
      this.user.set(user);
    });
  }

  /**
   * Get user initials for avatar display
   */
  getUserInitials(): string {
    const currentUser = this.user();
    if (!currentUser?.name) return '?';
    const nameParts = currentUser.name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return currentUser.name.substring(0, 2).toUpperCase();
  }

  /**
   * Toggle user dropdown menu
   */
  toggleUserMenu(): void {
    this.showUserMenu.update(show => !show);
  }

  /**
   * Close user menu when clicking outside
   */
  closeUserMenu(): void {
    this.showUserMenu.set(false);
  }

  /**
   * Toggle mobile navigation menu
   */
  toggleMobileMenu(): void {
    this.showMobileMenu.update(show => !show);
  }

  /**
   * Close mobile menu (when clicking outside or selecting a link)
   */
  closeMobileMenu(): void {
    this.showMobileMenu.set(false);
  }

  /**
   * Navigate and close mobile menu
   */
  navigateAndCloseMobileMenu(path: string): void {
    this.closeMobileMenu();
    this.router.navigate([path]);
  }

  /**
   * Logout user and redirect to login page
   */
  async logout(): Promise<void> {
    try {
      await this.authService.logout();
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }
}
